import React, { useState, useRef, useEffect } from 'react';
import ProgressSidebar from './ProgressSidebar';
import BrandLogo from './BrandLogo';

/**
 * Shell layout for the FPR update form wizard.
 *
 * Renders the sidebar on large screens and a mobile progress bar on small
 * screens, plus the main content area with Back / Next navigation buttons.
 */
export default function WizardLayout({
  currentStep,
  steps,
  onBack,
  onNext,
  onSkip,
  showSkip,
  isFirst,
  isLast,
  nextDisabled = false,
  submitLabel = 'Submit',
  saving,
  onStepClick,
  stepStatuses,
  clientName,
  onLogout,
  lockFutureSteps = false,
  children,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    if (!menuOpen) return;
    function handleClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  const totalSteps = steps.length;
  const progressSteps = totalSteps - 1; // exclude welcome from the count
  const welcomeIdx = steps.findIndex((s) => s.key === 'welcome');
  const isWelcome = currentStep === welcomeIdx;
  const isSchedule = steps[currentStep]?.key === 'schedule_meeting';
  // Step number ignores the welcome step so the count reads naturally.
  const stepNumber = currentStep < welcomeIdx ? currentStep + 1 : currentStep;
  const pct = isWelcome ? 0 : Math.round((stepNumber / progressSteps) * 100);

  return (
    <div className="cashflow-wizard-shell d-flex vh-100">
      <ProgressSidebar
        currentStep={currentStep}
        steps={steps}
        onStepClick={onStepClick}
        stepStatuses={stepStatuses}
        lockFutureSteps={lockFutureSteps}
      />

      <div className="cashflow-main flex-fill d-flex flex-column overflow-hidden">
        <header className="cashflow-topbar d-flex align-items-center px-4 py-2 border-bottom">
          <span className="d-lg-none me-2"><BrandLogo size={28} /></span>
          <span className="fw-semibold cashflow-brand-text d-lg-none me-auto small">
            FPR Update
          </span>
          <span className="text-muted small ms-auto d-none d-lg-block">
            Logged in as <strong>{clientName}</strong>
          </span>
          <button
            className="btn btn-link btn-sm text-muted ms-3 p-0"
            onClick={onLogout}
            title="Sign out"
          >
            <i className="bi bi-box-arrow-right" />
          </button>
        </header>

        {!isSchedule && (
          <div className="d-lg-none px-3 pt-2 position-relative" ref={menuRef}>
            <div className="d-flex justify-content-between align-items-center small text-muted mb-1">
              <div className="d-flex align-items-center gap-2">
                <button
                  className="btn btn-sm btn-outline-secondary p-0 px-1"
                  style={{ lineHeight: 1.2, fontSize: '1rem' }}
                  onClick={() => setMenuOpen((o) => !o)}
                  aria-label="Open step menu"
                  aria-expanded={menuOpen}
                >
                  <i className={`bi ${menuOpen ? 'bi-x-lg' : 'bi-list'}`} />
                </button>
                <span>{isWelcome ? 'Welcome' : `Step ${stepNumber} of ${progressSteps}`}</span>
              </div>
              <span>{steps[currentStep]?.label}</span>
            </div>
            <div className="progress" style={{ height: 6 }}>
              <div
                className="progress-bar bg-danger"
                style={{ width: `${pct}%` }}
                role="progressbar"
                aria-valuenow={pct}
                aria-valuemin={0}
                aria-valuemax={100}
              />
            </div>

            {menuOpen && (
              <div
                className="position-absolute start-0 end-0 bg-white border border-top-0 shadow-sm"
                style={{ top: '100%', zIndex: 1050, maxHeight: '60vh', overflowY: 'auto' }}
              >
                {steps.map((step, idx) => {
                  const isActive = idx === currentStep;
                  const status = stepStatuses?.[idx] || 'empty';
                  return (
                    <button
                      key={idx}
                      className={`d-flex align-items-center gap-2 w-100 text-start btn btn-sm px-3 py-2 border-0 rounded-0 ${isActive ? 'bg-danger text-white fw-semibold' : 'bg-white text-dark'}`}
                      style={{ borderBottom: '1px solid #eee' }}
                      onClick={() => {
                        setMenuOpen(false);
                        onStepClick && onStepClick(idx);
                      }}
                    >
                      <span style={{ width: 20, textAlign: 'center', fontSize: '0.75rem' }}>
                        {!isActive && status === 'complete' ? (
                          <i className="bi bi-check-lg text-success" />
                        ) : !isActive && status === 'partial' ? (
                          <i className="bi bi-pencil-fill text-warning" style={{ fontSize: '0.65rem' }} />
                        ) : idx === 0 ? (
                          <i className={`bi ${step.icon}`} style={{ fontSize: '0.7rem' }} />
                        ) : (
                          <span className="fw-semibold">{idx}</span>
                        )}
                      </span>
                      <span className="small">{step.label}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <main className="cashflow-step-content flex-fill overflow-auto px-4 py-4">
          {children}
        </main>

        <footer className="cashflow-footer border-top px-4 py-3 d-flex justify-content-between align-items-center">
          <button
            className="btn btn-outline-secondary"
            onClick={onBack}
            disabled={isFirst || saving}
          >
            <i className="bi bi-arrow-left me-1" />
            Back
          </button>

          <div className="flex-fill mx-3" style={{ maxWidth: 400 }}>
            {!isSchedule && (
              <div className="position-relative">
                <div className="progress" style={{ height: 22, borderRadius: 12 }}>
                  <div
                    className="progress-bar bg-danger"
                    style={{ width: `${pct}%`, borderRadius: 12, transition: 'width 0.3s ease' }}
                    role="progressbar"
                    aria-valuenow={pct}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  />
                </div>
                <span
                  className="position-absolute top-50 start-50 translate-middle fw-semibold"
                  style={{ fontSize: '0.75rem', color: pct > 45 ? '#fff' : '#666', zIndex: 1 }}
                >
                  {isWelcome ? 'Welcome' : `${stepNumber} / ${progressSteps}`}
                </span>
              </div>
            )}
          </div>

          <div className="d-flex gap-2">
            {showSkip && !isLast && (
              <button
                className="btn btn-outline-secondary px-3"
                onClick={onSkip}
                disabled={saving}
                title="Nothing has changed on this page — mark it complete and move on"
              >
                <i className="bi bi-check2 me-1" />
                Nothing changed
              </button>
            )}

            <button
              className={`btn ${isLast ? 'btn-success' : 'btn-danger'} px-4`}
              onClick={onNext}
              disabled={saving || nextDisabled}
              title={nextDisabled ? 'Please schedule your meeting before finishing.' : undefined}
            >
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Saving…
                </>
              ) : isLast ? (
                <>
                  {submitLabel} <i className="bi bi-check-lg ms-1" />
                </>
              ) : (
                <>
                  Next <i className="bi bi-arrow-right ms-1" />
                </>
              )}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
